<template>
  <div class="confirmation">
    <!-- 流程2：簡化版，只顯示打勾 + 已送出 -->
    <template v-if="simpleMode && !isReservationFailed">
      <div class="simple-success">
        <div class="success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M8 12l3 3 5-5"></path>
          </svg>
        </div>
        <h1 class="simple-title">已送出</h1>
      </div>
    </template>

    <!--完整版，顯示預約資訊 -->
    <template v-else>
      <template v-if="isReservationFailed">
        <h1 class="title-with-underline">掛號失敗</h1>
        <div class="reminder-grayscale guest-reminder">
          <p class="guest-reminder-line">{{ failureMessage }}</p>
        </div>
      </template>
      <template v-else>
        <h1 class="title-with-underline">掛號成功</h1>


        <!-- 預約通知 -->
        <div class="reminder-grayscale guest-reminder">
          <p v-for="(line, index) in guestReminderLines" :key="`guest-reminder-${index}`" class="guest-reminder-line">
            {{ line }}
          </p>
        </div>

        <div class="appointment-info">
          <div class="info-item" v-for="(item, index) in infoItems" :key="index">
            <span class="info-label" :class="item.labelColor">{{ item.label }}</span>
            
            <div v-if="Array.isArray(item.value)" class="info-value-group">
              <span class="info-value" :class="item.color" v-for="(val, vIndex) in item.value" :key="vIndex">{{ val }}</span>
            </div>
            <span v-else class="info-value" :class="item.color">{{ item.value }}</span>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
export default {
  name: 'Success',
  props: {
    simpleMode: {
      type: Boolean,
      default: false
    },
    clinicName: {
      type: String,
      default: '姜博文診所'
    },
    visitPurpose: {
      type: String,
      default: '體檢 / 健檢'
    },
    examTypeText: {
      type: String,
      default: '成人健檢'
    },
    visitTypeLabel: {
      type: String,
      default: '健檢種類'
    },
    showVisitPurpose: {
      type: Boolean,
      default: true
    },
    showDoctor: {
      type: Boolean,
      default: false
    },
    doctorName: {
      type: String,
      default: ''
    },
    visitTimeSlot: {
      type: String,
      default: '2025-09-11-afternoon'
    },
    patientName: {
      type: String,
      default: '王小明 (本人)'
    },
    selectedAddOns: {
      type: Array,
      default: () => []
    },
    totalCost: {
      type: Number,
      default: 0
    },
    carryItemsText: {
      type: String,
      default: ''
    },
    reservationInfoBlock: {
      type: Array,
      default: () => []
    },
    reservationResult: {
      type: [Object, Array],
      default: null
    },
    contactPhone: {
      type: String,
      default: ''
    }
  },
  computed: {
    isGuest() {
      return this.$store.getters['web/tongxin/isGuest'];
    },
    isReservationFailed() {
      return String(this.reservationResult && this.reservationResult.status ? this.reservationResult.status : '').toLowerCase() === 'fail';
    },
    failureMessage() {
      const result = this.reservationResult;
      const candidates = [
        result && result.message,
        result && result.data && result.data.message,
        Array.isArray(result && result.data) && result.data[0] && result.data[0].message
      ];
      const matched = candidates.find(item => typeof item === 'string' && item.trim());
      return matched || '預約失敗，請稍後再試';
    },
    sourceType() {
      return this.$store.getters['web/tongxin/sourceType'] || 'health';
    },
    currentFlowType() {
      if (this.simpleMode) return 'flow3';
      return this.visitTimeSlot === '由診所人員協助確認' ? 'flow2' : 'flow1';
    },
    formattedVisitDate() {
      if (!this.visitTimeSlot || this.visitTimeSlot === '由診所人員協助確認') {
        return '由診所人員協助確認';
      }

      const lastHyphenIndex = this.visitTimeSlot.lastIndexOf('-');
      if (lastHyphenIndex === -1) return this.visitTimeSlot;

      const datePart = this.visitTimeSlot.substring(0, lastHyphenIndex);
      const timeSlot = this.visitTimeSlot.substring(lastHyphenIndex + 1);
      const date = new Date(datePart);
      if (isNaN(date.getTime())) return '日期格式錯誤';

      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekday = ['日', '一', '二', '三', '四', '五', '六'][date.getDay()];
      const timeSlotText =
        timeSlot === 'morning' ? '上午診' :
        timeSlot === 'afternoon' ? '午診' :
        timeSlot === 'evening' ? '晚診' : '診';

      return `${month}/${day} (${weekday}) ${timeSlotText}`;
    },
    formattedTotalCost() {
      const amount = Number(this.totalCost)
      if (!Number.isFinite(amount) || amount <= 0) return ''
      return `${amount.toLocaleString('zh-TW')} 元`
    },
    resolvedReservationInfoBlock() {
      if (Array.isArray(this.reservationInfoBlock) && this.reservationInfoBlock.length > 0) {
        return this.reservationInfoBlock
      }

      const candidates = []
      const result = this.reservationResult

      if (result && typeof result === 'object') {
        candidates.push(result.infoBlock)
        candidates.push(result.summery && result.summery.infoBlock)
        candidates.push(result.data && result.data.infoBlock)
        candidates.push(result.data && result.data.summery && result.data.summery.infoBlock)
        if (Array.isArray(result.data) && result.data.length > 0) {
          candidates.push(result.data[0] && result.data[0].infoBlock)
          candidates.push(result.data[0] && result.data[0].summery && result.data[0].summery.infoBlock)
        }
      }

      const found = candidates.find(block => Array.isArray(block) && block.length > 0)
      return found || []
    },
    outpatientNumber() {
      if (this.sourceType !== 'outpatient') return ''
      if (!Array.isArray(this.resolvedReservationInfoBlock) || this.resolvedReservationInfoBlock.length === 0) return ''

      const target = this.resolvedReservationInfoBlock.find(item => String(item && item.title ? item.title : '').trim() === '看診號')
      return target && target.value !== null && target.value !== undefined
        ? String(target.value).trim()
        : ''
    },
    resolvedVisitPurpose() {
      if (typeof this.visitPurpose === 'string' && this.visitPurpose.trim()) {
        return this.visitPurpose.trim();
      }
      if (this.sourceType === 'vaccine') return '注射疫苗';
      if (this.sourceType === 'outpatient') return '一般看診';
      return '體檢 / 健檢';
    },
    visitTypeValueColor() {
      if (this.sourceType === 'outpatient') return 'text-type-black';
      if (this.sourceType === 'vaccine' || this.sourceType === 'health') return 'text-data-present';
      return 'text-type-black';
    },
    infoItems() {
      const items = [
        { label: '就診院所', value: this.clinicName },
        { label: '就診時段', value: this.formattedVisitDate, color: this.formattedVisitDate === '由診所人員協助確認' ? 'text-data-missing' : 'text-data-present' },
        { label: '就診人', value: this.patientName },
        { label: this.visitTypeLabel, value: this.examTypeText, color: this.visitTypeValueColor },
      ];

      if (this.selectedAddOns && this.selectedAddOns.length > 0) {
        items.push({
          label: '加購項目',
          value: this.selectedAddOns,
          color: 'text-data-present'
        });
      }

      if (this.formattedTotalCost && this.sourceType !== 'outpatient') {
        items.push({
          label: '費用',
          value: this.formattedTotalCost,
          color: 'text-data-present'
        });
      }

      items.splice(3, 0, { label: '就診目的', value: this.resolvedVisitPurpose });

      if (this.sourceType === 'outpatient' && this.outpatientNumber) {
        const purposeIndex = items.findIndex(item => item.label === '就診目的');
        const insertIndex = purposeIndex >= 0 ? purposeIndex + 1 : 4;
        items.splice(insertIndex, 0, {
          label: '號碼',
          value: this.outpatientNumber,
          color: 'text-data-present'
        });
      }

      if (this.showDoctor) {
        items.push({
          label: '看診醫師',
          value: this.doctorName || '由診所安排',
          color: 'text-type-black'
        });
      }
      
      return items;
    },
    guestReminderLines() {
      const result = this.reservationResult;
      const noticeFromApi = [
        result && result.notice,
        result && result.data && result.data.notice,
        Array.isArray(result && result.data) && result.data[0] && result.data[0].notice
      ].find(item => typeof item === 'string' && item.trim());

      return noticeFromApi ? [noticeFromApi] : [];
    },
  }
}
</script>

<style lang="scss" scoped>
.confirmation {
  --wide-row-max-width: 960px;
  --wide-row-side-gap: 56px;
}

/* 流程2：簡化版成功頁 */
.simple-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  text-align: center;
}

.success-icon {
  width: 72px;
  height: 72px;
  color: #2a9d8f;
  margin-bottom: 20px;

  svg {
    width: 100%;
    height: 100%;
  }
}

.simple-title {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0;
}

/* 流程1：完整版成功頁 */
.appointment-info {
  background: #ffffff;
  border-radius: 12px;
  padding: 0;
  margin-bottom: 24px;
  overflow: hidden;
  width: 100%;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 768px) {
    overflow: visible;
  }
}

.info-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  border-bottom: 1px solid #e9ecef;
  padding: 10px 14px;
  text-align: left;
  position: relative;
  z-index: 0;

  &:nth-child(odd) {
    background: #f8f9fa;
  }

  &:nth-child(even) {
    background: #efecec;
  }

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 12px 16px;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;
      width: min(var(--wide-row-max-width), calc(100vw - var(--wide-row-side-gap)));
      transform: translateX(-50%);
      z-index: -1;
    }
  }

  &:last-child {
    border-bottom: none;
  }
}

@media (min-width: 768px) {
  .info-item:nth-child(odd)::before {
    background: #f8f9fa;
  }

  .info-item:nth-child(even)::before {
    background: #efecec;
  }
}

.info-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  text-align: left;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 18px;
    flex: 0 0 170px;
    width: auto;
  }
}

.info-value {
  font-size: 16px;
  color: #333;
  font-weight: 700;
  text-align: left;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 18px;
    flex: 0 0 220px;
    width: auto;
    text-align: right;
  }
}

.text-data-present {
  color: #57A572;
}

.text-data-missing {
  color: #FF0000;
}

.text-type-black {
  color: #000000;
}

.info-value-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  text-align: left;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;

  @media (min-width: 768px) {
    align-items: flex-end;
    flex: 0 0 220px;
    width: auto;
    text-align: right;
  }

  .info-value {
    flex: none;
    width: auto;
  }
}

.guest-reminder {
  margin-top: 12px;
}

.guest-reminder-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #333;
}

.guest-reminder-line {
  margin: 0 0 6px;
  color: #333;
  line-height: 1.7;
}
</style>
